import { useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { ArrowRight, CaretDown, Check } from '@phosphor-icons/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useSEO } from '@/hooks/useSEO';
import PublicNav from '@/components/layout/PublicNav';
import PublicFooter from '@/components/layout/PublicFooter';

gsap.registerPlugin(ScrollTrigger);

const FAQS = [
  {
    q: 'Who is YuHasPro built for?',
    a: 'YuHasPro is designed for coaching classes, schools, training institutes, and education franchises across India. Whether you run a single centre or 100+ branches, YuHasPro scales with you.',
  },
  {
    q: 'How do we get started?',
    a: 'Simply fill out the contact form and our team will schedule a personalized demo. After the demo, we\'ll set up your account and onboard your team — typically within 48 hours.',
  },
  {
    q: 'Is my data secure?',
    a: 'Yes. All data is encrypted in transit and at rest. We run on secure cloud infrastructure with daily backups, role-based access control, and full audit logging.',
  },
  {
    q: 'Can I manage multiple branches?',
    a: 'Absolutely. YuHasPro supports unlimited branches with role-based access, branch-level analytics, and centralised control. Perfect for education groups and franchises.',
  },
  {
    q: 'What kind of support do you offer?',
    a: 'Every customer gets dedicated onboarding support. Ongoing support includes email, chat, and phone assistance. Enterprise customers get a dedicated account manager.',
  },
  {
    q: 'Can YuHasPro integrate with our existing tools?',
    a: 'Yes — YuHasPro integrates with WhatsApp, SMS providers, email platforms, and payment gateways out of the box. Custom integrations are available for enterprise customers.',
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border" data-testid={`faq-${q.slice(0, 20).toLowerCase().replace(/\s/g, '-')}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-5 text-left group">
        <span className="text-sm font-semibold text-foreground group-hover:text-foreground/80 pr-4">{q}</span>
        <CaretDown size={16} weight="bold" className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}>
        <p className="text-[13px] text-muted-foreground leading-relaxed">{a}</p>
      </div>
    </div>
  );
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const containerRef = useRef(null);

  useSEO({
    title: 'Pricing — YuHasPro Admissions CRM',
    description: 'Pricing tailored to your institute. Contact our sales team for a personalized quote and demo of YuHasPro.',
  });

  useGSAP(() => {
    const ctx = gsap.context(() => {
      const heroTl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      heroTl
        .from('.pp-badge', { y: 20, opacity: 0, duration: 0.5 })
        .from('.pp-headline', { y: 40, opacity: 0, duration: 0.7 }, '-=0.2')
        .from('.pp-subtitle', { y: 20, opacity: 0, duration: 0.5 }, '-=0.3');

      gsap.from('.pp-faq', {
        y: 30, opacity: 0, duration: 0.6, immediateRender: false,
        scrollTrigger: { trigger: '.pp-faq', start: 'top 80%' },
      });
    }, containerRef);
    return () => ctx.revert();
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="bg-background text-foreground overflow-x-hidden">
      <PublicNav />

      <main>
        {/* ═══ HERO ═══ */}
        <section className="pt-32 pb-10 px-6 lg:px-12 max-w-4xl mx-auto text-center">
          <div className="pp-badge inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground mb-6">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E04B2A' }} /> Tailored for you
          </div>
          <h1 className="pp-headline font-display font-extrabold text-[clamp(2.2rem,5vw,4rem)] tracking-[-0.03em] leading-[1.05] text-foreground">
            Pricing tailored to<br />your institute.
          </h1>
          <p className="pp-subtitle text-lg text-muted-foreground mt-4 max-w-xl mx-auto leading-relaxed">
            Every institute is different. We'll work with you to find a plan that fits your size, branches, and needs. No hidden fees, no surprises.
          </p>
        </section>

        {/* ═══ CONTACT CTA ═══ */}
        <section className="py-16 px-6 lg:px-12 max-w-3xl mx-auto">
          <div className="rounded-2xl p-10 border border-border text-center relative overflow-hidden" style={{ background: theme === 'dark' ? 'linear-gradient(135deg, rgba(224,75,42,0.08), transparent)' : 'linear-gradient(135deg, rgba(224,75,42,0.05), transparent)' }}>
            <div className="space-y-5">
              {[
                'Personalized demo of the full platform',
                'Custom pricing based on your branches and team size',
                'Dedicated onboarding and migration support',
              ].map((item) => (
                <div key={item} className="flex items-center justify-center gap-3">
                  <Check size={14} weight="bold" style={{ color: '#E04B2A' }} />
                  <span className="text-[13px] text-foreground">{item}</span>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-3 mt-8">
              <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="pricing-cta-contact">
                Contact Sales <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          </div>
        </section>

        {/* ═══ FAQ ═══ */}
        <section className="pp-faq py-20 px-6 lg:px-12 max-w-3xl mx-auto" data-testid="pricing-faq">
          <div className="text-center mb-10">
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] mb-3" style={{ color: '#E04B2A' }}>FAQ</p>
            <h2 className="font-display text-3xl font-extrabold text-foreground tracking-tight">Common questions</h2>
          </div>
          <div className="border-t border-border">
            {FAQS.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </section>

        {/* ═══ CTA ═══ */}
        <section className="py-24 px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center rounded-2xl p-12 border border-border relative overflow-hidden" style={{ background: theme === 'dark' ? 'linear-gradient(135deg, rgba(224,75,42,0.08), transparent)' : 'linear-gradient(135deg, rgba(224,75,42,0.05), transparent)' }}>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-foreground tracking-tight leading-[1.1]">Ready to see YuHasPro in action?</h2>
            <p className="text-base text-muted-foreground mt-4 max-w-md mx-auto">Book a personalized demo. Our team will walk you through the platform and answer all your questions.</p>
            <div className="flex justify-center gap-3 mt-8">
              <button onClick={() => navigate('/contact')} className="h-12 px-8 rounded-lg text-sm font-bold text-white flex items-center gap-2 transition-transform hover:scale-[1.02]" style={{ backgroundColor: '#E04B2A' }} data-testid="pricing-cta-final">
                Request a Demo <ArrowRight size={16} weight="bold" />
              </button>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
